import { useState, useEffect } from 'react'
import {
  type User as FirebaseUser,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth'
import {
  doc,
  getDocFromServer,
  getDocs,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { auth, db, functions } from '@/lib/firebase'
import type { User, LinkedProvider } from '@/types'
import { migrateUserToMultiOrg, findOrAddOrganization } from '@/lib/multiOrgHelpers'

export function useAuthState() {
  const [user, setUser] = useState<FirebaseUser | null>(null)
  const [userProfile, setUserProfile] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser)

      if (firebaseUser) {
        try {
          const profile = await getOrCreateUserProfile(firebaseUser)
          setUserProfile(profile)
          setAuthError(null)
        } catch (e) {
          // getOrCreateUserProfile logt zelf uit bij geen-profiel/verkeerd-domein.
          // Bewaar de melding zodat de Login-pagina ze kan tonen i.p.v. stil
          // terug te keren naar het loginvenster (oorzaak van de "login-loop").
          setUserProfile(null)
          setAuthError(e instanceof Error ? e.message : 'Aanmelden mislukt.')
        }
      } else {
        setUserProfile(null)
      }

      setLoading(false)
    })

    return unsubscribe
  }, [])

  return { user, firebaseUser: user, userProfile, loading, authError }
}

async function getOrCreateUserProfile(firebaseUser: FirebaseUser): Promise<User> {
  const ref = doc(db, 'users', firebaseUser.uid)
  // === MULTI-ORG SUPPORT ===
  // currentDomain is synchroon afleidbaar, dus het profiel ophalen en het
  // huidige org-domein opzoeken zijn onafhankelijke reads — parallel i.p.v. na
  // elkaar. Elke sequentiële Firestore-call telt op, vooral merkbaar op
  // netwerken/browsers met hogere latency per call (o.a. Safari).
  const currentDomain = firebaseUser.email?.split('@')[1] ?? ''
  const [snap, currentOrg] = await Promise.all([
    getDocFromServer(ref),
    findOrgForDomain(currentDomain),
  ])

  if (snap.exists()) {
    const existingUser = snap.data() as User
    const updates: Record<string, any> = {
      laatsteLogin: serverTimestamp(),
    }

    if (currentOrg) {
      // Migreer oude user structuur naar multi-org als nodig
      const migrationUpdates = migrateUserToMultiOrg(existingUser)
      if (Object.keys(migrationUpdates).length > 0) {
        Object.assign(updates, migrationUpdates)
        console.log('Migrating user to multi-org structure:', firebaseUser.uid)
      }

      // Check of we een nieuwe/bestaande org moeten toevoegen. Rol is bewust
      // altijd 'user' (zelfde conventie als de linked-email-upgrade hieronder,
      // regel ~162) — automatische, incidentele domein-matching mag nooit een
      // rol toekennen/heractiveren, laat staan de rol van een andere, actieve
      // org hergebruiken voor deze org. Een echte rol komt altijd van een
      // bewuste beheerdersactie (addUserToOrganization/updateUserOrgRole).
      // (ontdekt 4 okt 2026, zie memory toegangsniveau-sync-removeuserfromorg)
      const { organizations, activeOrgId, needsUpdate } = findOrAddOrganization(
        { ...existingUser, ...updates } as User,
        currentOrg.orgId,
        'user',
        currentDomain
      )

      if (needsUpdate) {
        updates.organizations = organizations
        updates.activeOrgId = activeOrgId
        console.log('Updated organizations for user:', firebaseUser.uid, organizations)
      }

      // Update backward compatibility velden
      updates.orgId = activeOrgId
      updates.organisatie = currentOrg.naam
      updates.domein = currentDomain
    }

    // Sync provider data als er meerdere providers zijn
    if (firebaseUser.providerData && firebaseUser.providerData.length > 1) {
      const providers: LinkedProvider[] = firebaseUser.providerData
        .filter(p => p.email && p.providerId)
        .map(p => ({
          providerId: p.providerId!,
          email: p.email!,
          linkedAt: serverTimestamp() as never,
          ...(p.displayName && { displayName: p.displayName }),
          ...(p.photoURL && { photoURL: p.photoURL }),
        }))

      const uniqueEmails = Array.from(new Set(providers.map(p => p.email)))

      if (providers.length > 0) {
        updates.linkedProviders = providers
        updates.linkedEmails = uniqueEmails
      }
    }

    // === UPGRADE LINKED EMAILS TO ORGANIZATIONAL MEMBERSHIP ===
    // Check alle linkedEmails of ze nu tot een gekende organisatie behoren
    // Dit vangt het scenario op waar user een email linkt VOOR de org bestaat
    const currentLinkedEmails = (updates.linkedEmails || existingUser.linkedEmails || []) as string[]
    const currentOrganizations = (updates.organizations || existingUser.organizations || []) as any[]

    if (currentLinkedEmails.length > 0) {
      // Enkel de org-opzoekingen (netwerk) parallelliseren; de eigenlijke
      // org-toevoeging blijft nadien sequentieel doorlopen (synchroon, geen
      // await meer) zodat een latere iteratie nog steeds rekening houdt met
      // een org die een eerdere iteratie in dezelfde login al toevoegde.
      const relevanteLinkedEmails = currentLinkedEmails.filter(email => email.split('@')[1] !== currentDomain)
      const linkedOrgResultaten = await Promise.all(
        relevanteLinkedEmails.map(async linkedEmail => {
          const linkedDomain = linkedEmail.split('@')[1]
          return { linkedEmail, linkedDomain, linkedOrg: await findOrgForDomain(linkedDomain) }
        })
      )

      let organizationsUpdated = false
      let updatedOrganizations = [...currentOrganizations]

      for (const { linkedEmail, linkedDomain, linkedOrg } of linkedOrgResultaten) {
        if (linkedOrg) {
          // Check of user al lid is van deze org
          const alreadyMember = updatedOrganizations.some(o => o.orgId === linkedOrg.orgId)

          if (!alreadyMember) {
            console.log(`Upgrading linked email ${linkedEmail} to organizational membership for ${linkedOrg.naam}`)

            // Voeg org toe
            const { organizations: newOrgs } = findOrAddOrganization(
              { ...existingUser, ...updates, organizations: updatedOrganizations } as User,
              linkedOrg.orgId,
              'user',
              linkedDomain
            )

            updatedOrganizations = newOrgs
            organizationsUpdated = true
          }
        }
      }

      if (organizationsUpdated) {
        updates.organizations = updatedOrganizations
        console.log('Auto-upgraded linked emails to organizations:', firebaseUser.uid)
      }
    }

    await updateDoc(ref, updates)

    // Return updated user
    return { ...existingUser, ...updates } as User
  }

  // === CLAIM PRE-AANGEMAAKT PROFIEL ===
  // Voor er gevallen wordt op auto-registratie: kijk of een admin al een
  // profiel heeft klaargezet voor deze email (met een precreated_-id). Zo ja,
  // verhuist een Cloud Function dat profiel atomisch naar de echte UID.
  try {
    const claimFn = httpsCallable<{}, { claimed: boolean; reason?: string }>(functions, 'claimPrecreatedProfile')
    const claimRes = await claimFn({})
    if (claimRes.data?.claimed) {
      const newSnap = await getDocFromServer(ref)
      if (newSnap.exists()) return newSnap.data() as User
    }
  } catch (e) {
    console.warn('claimPrecreatedProfile mislukt (niet kritiek, ga door met auto-registratie):', e)
  }

  // === TIJDELIJKE DOMEIN-AUTO-REGISTRATIE ===
  // Enkel actief als een platform-admin dit per org expliciet heeft aangezet
  // (orgFeatures.domeinAutoRegistratie). E-mail wordt server-side uit het
  // auth-token gehaald, nooit hier meegegeven — zie autoRegisterByDomain.
  try {
    const autoFn = httpsCallable<{}, { registered: boolean; reason?: string }>(functions, 'autoRegisterByDomain')
    const autoRes = await autoFn({})
    if (autoRes.data?.registered) {
      const newSnap = await getDocFromServer(ref)
      if (newSnap.exists()) return newSnap.data() as User
    }
  } catch (e) {
    console.warn('autoRegisterByDomain mislukt (niet kritiek, ga door):', e)
  }

  // === GEEN PROFIEL GEVONDEN: AUTO-REGISTRATIE GESLOTEN ===
  // Geen doc op echte UID, geen pre-aangemaakt profiel om te claimen.
  // Toegang vereist dat een schooladmin (of platform-admin) eerst een
  // profiel aanmaakt. Dit voorkomt dat een willekeurige gebruiker met een
  // bekend schooldomein zich kan registreren.
  await signOut(auth)
  throw new UserNotProvisionedError(firebaseUser.email ?? '')
}

async function findOrgForDomain(domein: string): Promise<{ orgId: string; naam: string } | null> {
  if (!domein) return null

  const snap = await getDocs(
    query(
      collection(db, 'organizations'),
      where('toegestaaneDomeinen', 'array-contains', domein),
      where('actief', '==', true)
    )
  )

  if (snap.empty) return null
  // Sommige domeinen zijn bewust gedeeld tussen organisaties (bv. een
  // scholengemeenschap zonder eigen domein die dat van de scholengroep
  // hergebruikt). Bij zo'n ambiguë match nergens automatisch een org kiezen —
  // dit bepaalt anders willekeurig welke org-lidmaatschap/rol een gebruiker
  // bij elke login krijgt toegevoegd. Gebruikers op zo'n domein moeten
  // handmatig (precreated) in de juiste org gezet worden.
  if (snap.docs.length > 1) return null
  const data = snap.docs[0].data()
  return { orgId: data.orgId as string, naam: data.naam as string }
}

export class DomainNotAllowedError extends Error {
  constructor(public domein: string) {
    super(`Domein @${domein} heeft geen toegang tot dit platform.`)
    this.name = 'DomainNotAllowedError'
  }
}

export class UserNotProvisionedError extends Error {
  constructor(public email: string) {
    super(`Geen profiel gevonden voor ${email}. Vraag je schooladmin om je toe te voegen aan het platform.`)
    this.name = 'UserNotProvisionedError'
  }
}

export async function signInWithGoogle(): Promise<void> {
  const provider = new GoogleAuthProvider()
  // Geen hd-restrictie — domeinvalidatie gebeurt via Firestore organizations
  provider.setCustomParameters({ prompt: 'select_account' })
  await signInWithPopup(auth, provider)
}

export async function signInWithMicrosoft(): Promise<void> {
  const provider = new OAuthProvider('microsoft.com')
  provider.setCustomParameters({
    tenant: 'organizations', // Alle Microsoft 365-tenants, geen persoonlijke accounts
    prompt: 'select_account',
  })
  await signInWithPopup(auth, provider)
}

export async function signInWithEmail(email: string, password: string): Promise<void> {
  await signInWithEmailAndPassword(auth, email, password)
}

export async function logout(): Promise<void> {
  await signOut(auth)
}

/**
 * Switch naar een andere organisatie
 */
export async function switchOrganization(orgId: string): Promise<void> {
  const user = auth.currentUser
  if (!user) throw new Error('Niet ingelogd')

  const ref = doc(db, 'users', user.uid)
  await updateDoc(ref, {
    activeOrgId: orgId,
    orgId, // Backward compatibility
  })

  // Refresh pagina om nieuwe org context te laden
  window.location.reload()
}
